<template>
  <canvas v-if="season?.name === 'gregorian'" ref="canvas" class="fixed inset-0 z-[994] w-full h-full pointer-events-none" aria-hidden="true" />
</template>

<script setup>
// Light, continuous snowfall over the site while the (cold) Gregorian New Year theme is active.
// Three depths of flakes, from tiny distant dots to a few slowly turning crystals. It starts once
// the page is idle, is skipped for reduced motion, and the browser pauses it in background tabs.
const season = useSeason()
const canvas = ref(null)

let ctx, width, height, raf = 0, last = 0
let flakes = []
let color = '255 255 255'

const rand = (min, max) => min + Math.random() * (max - min)

function resize() {
  const dpr = Math.min(window.devicePixelRatio || 1, 1.5)
  width = window.innerWidth
  height = window.innerHeight
  canvas.value.width = width * dpr
  canvas.value.height = height * dpr
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
  const count = Math.round(Math.min(90, Math.max(30, width / 18)))
  while (flakes.length < count) flakes.push(flake(rand(-height, height)))
  flakes.length = count
}

function flake(y = rand(-40, -10)) {
  const depth = Math.random() // 0 = far, 1 = near
  return {
    x: rand(0, width),
    y,
    r: 0.8 + depth * 2.6,
    speed: 0.25 + depth * 0.9,
    sway: rand(0.3, 1.2),
    phase: rand(0, Math.PI * 2),
    alpha: 0.25 + depth * 0.55,
    // the nearest flakes are drawn as six-armed crystals
    crystal: depth > 0.88,
    spin: rand(-0.01, 0.01),
    angle: rand(0, Math.PI),
  }
}

function drawCrystal(f) {
  const s = f.r * 2.4
  ctx.save()
  ctx.translate(f.x, f.y)
  ctx.rotate(f.angle)
  ctx.strokeStyle = `rgb(${color} / ${f.alpha})`
  ctx.lineWidth = 1
  ctx.beginPath()
  for (let i = 0; i < 6; i++) {
    const a = (i * Math.PI) / 3
    const cx = Math.cos(a), cy = Math.sin(a)
    ctx.moveTo(0, 0)
    ctx.lineTo(cx * s, cy * s)
    // small side branches
    const bx = cx * s * 0.55, by = cy * s * 0.55
    ctx.moveTo(bx, by)
    ctx.lineTo(bx + Math.cos(a + 0.7) * s * 0.3, by + Math.sin(a + 0.7) * s * 0.3)
    ctx.moveTo(bx, by)
    ctx.lineTo(bx + Math.cos(a - 0.7) * s * 0.3, by + Math.sin(a - 0.7) * s * 0.3)
  }
  ctx.stroke()
  ctx.restore()
}

function frame(now) {
  // Scale motion by elapsed time so the fall speed is the same at any refresh rate.
  const step = Math.min(3, (now - (last || now)) / 16.7)
  last = now
  ctx.clearRect(0, 0, width, height)
  for (const f of flakes) {
    f.phase += 0.01 * step
    f.y += f.speed * step
    f.x += Math.sin(f.phase) * f.sway * 0.35 * step
    f.angle += f.spin * step
    if (f.y > height + 10) Object.assign(f, flake())
    if (f.crystal) {
      drawCrystal(f)
    } else {
      ctx.fillStyle = `rgb(${color} / ${f.alpha})`
      ctx.beginPath()
      ctx.arc(f.x, f.y, f.r, 0, Math.PI * 2)
      ctx.fill()
    }
  }
  raf = requestAnimationFrame(frame)
}

function start() {
  if (!canvas.value || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
  ctx = canvas.value.getContext('2d')
  // mostly white, tinted slightly towards the frost accent
  const accent = getComputedStyle(document.documentElement).getPropertyValue('--primary-rgb').trim()
  if (accent) color = accent.split(' ').map((c) => Math.round((+c + 255 * 2) / 3)).join(' ')
  resize()
  window.addEventListener('resize', resize)
  raf = requestAnimationFrame(frame)
}

// The season is resolved on the client after hydration (app.vue), so wait for it.
watch(season, (value) => {
  if (value?.name !== 'gregorian' || raf) return
  const idle = () => onIdle(start, 3000)
  if (document.readyState === 'complete') idle()
  else window.addEventListener('load', idle, { once: true })
}, { immediate: true })

onBeforeUnmount(() => {
  cancelAnimationFrame(raf)
  window.removeEventListener('resize', resize)
})
</script>
